import React from 'react'
import { makeStyles } from '@material-ui/core'
import type { Theme } from '@material-ui/core/styles'
import type { StyleClasses } from '../../styles'
import type { Details } from '../DetailsContainer/DetailsContainer'
import { useSnackbar } from 'notistack'
import FileCopyIcon from '@material-ui/icons/FileCopyOutlined'
import LaunchIcon from '@material-ui/icons/Launch'
import Typography from '../Typography'
import classNames from 'classnames'

const useStyles = makeStyles<Theme, DetailDocListProps>(
  (theme) => ({
    root: {},
    noDetailsItem: {
      display: 'flex',
      justifyContent: 'center',
      height: 200,
      alignItems: 'center',
      whiteSpace: 'pre-wrap',
      color: theme.palette.text.disabled
    },
    detailsCopyIcon: {
      fontSize: '1.5rem',
      marginLeft: theme.spacing(),
      color: theme.paintscout.palette.brand.Neutral.P80
    },
    detailsDocItem: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: theme.spacing(2),
      paddingLeft: theme.spacing(2),
      paddingRight: theme.spacing(2),
      '&:hover': {
        cursor: 'pointer',
        backgroundColor: theme.paintscout.palette.brand.Neutral.P96
      },
      '&:not(:last-child)': {
        borderBottom: `1px solid ${theme.palette.divider}`
      }
    },
    linkHover: {
      '&:hover': {
        cursor: 'pointer',
        color: theme.paintscout.palette.brand.Neutral.P40
      }
    }
  }),
  { name: 'DetailDocList' }
)

export interface DetailDocListProps {
  classes?: StyleClasses<typeof useStyles>
  details: Details[]
}

function DetailDocList(props: DetailDocListProps) {
  const classes = useStyles(props)
  const { details } = props
  const { enqueueSnackbar } = useSnackbar()
  const handleCopy = (value: string, title: string) => {
    navigator.clipboard
      .writeText(value)
      .then(() => {
        enqueueSnackbar(`${title} Copied`, { variant: 'success' })
      })
      .catch(() => {
        enqueueSnackbar(`Failed to copy ${title}`, { variant: 'error' })
      })
  }
  return (
    <div>
      {details.length === 0 && (
        <div className={classes.noDetailsItem}>
          <Typography variant="subtitle1">No details found.</Typography>
        </div>
      )}
      {details.map((detail, index) => (
        <div
          key={index}
          onClick={() =>
            detail.canCopy && !detail.link
              ? handleCopy(detail.value as string, detail.title)
              : detail.link && !detail.canCopy
                ? detail.link()
                : null
          }
          className={classes.detailsDocItem}
        >
          <div>
            <Typography style={{ display: 'flex', alignItems: 'center' }} variant={'body2'} color="textSecondary">
              {detail.title}
              {detail?.icon ? detail.icon : null}
            </Typography>
            <Typography
              variant={'body1'}
              onClick={detail.link ? detail.link : null}
              className={classNames({
                [classes.linkHover]: detail?.link
              })}
            >
              {detail.value}
            </Typography>
          </div>
          <div>
            {detail.canCopy && (
              <FileCopyIcon
                className={classes.detailsCopyIcon}
                onClick={() =>
                  detail.canCopy && detail.link ? handleCopy(detail.value as string, detail.title) : null
                }
              />
            )}
            {detail.link && !detail.canCopy && <LaunchIcon className={classes.detailsCopyIcon} onClick={detail.link} />}
          </div>
        </div>
      ))}
    </div>
  )
}

export default DetailDocList
